import { ogImageMeta } from "@/lib/seo/og-image";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { LocaleLink } from "@/components/site/LocaleLink";
import { ArrowRight, Compass } from "lucide-react";
import { INDUSTRIES } from "@/content/industries";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Reveal, RevealItem } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";
import { MediaAnalysisHero } from "@/components/site/MediaAnalysisHero";
import { SubPagePillNav, INDUSTRY_PILL_ITEMS } from "@/components/site/SubPagePillNav";

import { pageSchemas } from "@/lib/seo/page-schema";
type IndustryPath =
  | "/industries"
  | "/industries/fmcg"
  | "/industries/pharma"
  | "/industries/retail"
  | "/industries/fintech"
  | "/industries/automotive";

export const Route = createFileRoute("/industries")({
  head: () => ({
    meta: [
      { title: "Industries - Media Monitoring & Analysis | Perceptica" },
      {
        name: "description",
        content:
          "Perceptica delivers media monitoring and media analysis tailored to fintech, pharma, FMCG, retail and automotive across Southeast Europe.",
      },
      { property: "og:title", content: "Industries - Media Monitoring & Analysis | Perceptica" },
      {
        property: "og:description",
        content: "Perceptica delivers media monitoring and media analysis tailored to fintech, pharma, FMCG, retail and automotive across Southeast Europe.",
      },

      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Industries - Media Monitoring & Analysis | Perceptica" },
      { name: "twitter:description", content: "Perceptica delivers media monitoring and media analysis tailored to fintech, pharma, FMCG, retail and automotive across Southeast Europe." },
      { property: "og:url", content: "https://perceptica.com/industries" },
      ...ogImageMeta("en", "/industries"),
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/industries", collection: { name: "Industries we serve", description: "Industries Perceptica supports" } }),
  }),
  component: IndustriesLayout,
});

function IndustriesLayout() {
  const { pathname } = useLocation();
  const isIndex = pathname === "/industries" || pathname === "/bg/industries";

  if (isIndex) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main id="main">
          <MediaAnalysisHero
            eyebrow="Industries"
            headlineLead="Tailored Media Intelligence for the "
            headlineHighlight="Sectors We Know Best"
            description="Every industry has its own regulators, audiences and crisis windows. Perceptica's analysts pair our platform with category expertise so the insight arrives ready to act on."
            ctas={[
              { label: "Talk to us", to: "/request-a-demo" },
              { label: "Explore industries", href: "#industries" },
            ]}
          />
          <SubPagePillNav items={INDUSTRY_PILL_ITEMS} />

          <section id="industries" className="scroll-mt-24 bg-background py-24 lg:py-32">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
              <Reveal>
                <div className="flex flex-col items-start gap-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                    By Industry
                  </p>
                  <h2 className="font-display max-w-3xl text-[clamp(1.85rem,3vw,2.5rem)] leading-[1.1] text-foreground">
                    Pick the sector you operate in
                  </h2>
                  <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-foreground/70">
                    Each industry view is built around the questions, competitors and risk signals that matter to your category.
                  </p>
                </div>
              </Reveal>

              <Reveal stagger as="ul" className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-border/60 bg-border/60 sm:grid-cols-2 lg:grid-cols-3">
                {INDUSTRIES.map((i, idx) => {
                  const Icon = i.icon;
                  return (
                    <li key={i.slug} className="h-full">
                      <RevealItem className="h-full">
                        <LocaleLink
                          to={i.href as IndustryPath}
                          className="group relative flex h-full flex-col bg-background p-8 transition-colors hover:bg-secondary/40 lg:p-10"
                        >
                          <p className="text-gradient-brand font-display text-[28px] font-semibold leading-none tracking-[-0.01em]">
                            {String(idx + 1).padStart(2, "0")}
                          </p>
                          <span className="mt-6 inline-flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-accent">
                            <Icon className="h-5 w-5" />
                          </span>
                          <h3 className="font-display mt-6 text-[20px] leading-snug tracking-[-0.01em] text-foreground">
                            {i.name}
                          </h3>
                          <p className="mt-3 text-[13.5px] leading-relaxed text-muted-foreground">
                            {i.blurb}
                          </p>
                          <span className="mt-auto inline-flex items-center gap-1.5 pt-8 text-[12px] font-semibold uppercase tracking-[0.16em] text-accent">
                            Explore
                            <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                          </span>
                          <span
                            aria-hidden
                            className="absolute inset-x-0 top-0 h-px origin-left scale-x-0 bg-gradient-brand transition-transform duration-500 group-hover:scale-x-100"
                          />
                        </LocaleLink>
                      </RevealItem>
                    </li>
                  );
                })}
              </Reveal>
            </div>
          </section>

          <section className="relative bg-background pt-20 pb-28 lg:pt-24 lg:pb-32">
            <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
              <Reveal>
                <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground shadow-[0_30px_80px_-30px_rgba(15,12,40,0.5)]">
                  <div className="grid-overlay" />
                  <div className="noise-overlay" />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -top-24 -left-24 h-[360px] w-[360px] rounded-full opacity-40 blur-[120px]"
                    style={{ background: "radial-gradient(circle, #6B5BF5 0%, transparent 70%)" }}
                  />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -bottom-32 -right-24 h-[380px] w-[380px] rounded-full opacity-40 blur-[120px]"
                    style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
                  />
                  <div className="relative grid gap-12 px-8 py-16 lg:grid-cols-[1.4fr_1fr] lg:items-center lg:gap-16 lg:px-16 lg:py-20">
                    <div>
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                        <Compass className="h-3.5 w-3.5 text-accent" />
                        Tailored to your sector
                      </span>
                      <h2 className="font-display mt-6 max-w-[22ch] text-[clamp(1.85rem,3.2vw,2.75rem)] leading-[1.05] text-white">
                        Want intelligence shaped around your industry?
                      </h2>
                      <p className="mt-6 max-w-xl text-[15.5px] leading-relaxed text-white/70">
                        Tell us about your category and we will show you what Perceptica can surface for your brand in a live, tailored demo.
                      </p>
                    </div>
                    <div className="flex flex-col gap-4 lg:items-end">
                      <Button asChild size="xl" className="w-full lg:w-auto">
                        <LocaleLink to="/request-a-demo">
                          Let's talk
                          <ArrowRight className="h-4 w-4" />
                        </LocaleLink>
                      </Button>
                      <p className="text-[12px] uppercase tracking-[0.18em] text-white/80">
                        Most requests receive a reply from us within one business day.
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </section>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <Outlet />
  );
}
